import {
  CareerSiteFiltersOption as BaseCareerSiteFiltersOption,
  CareerSiteSettings,
} from 'api-clients/monolith';
import { getFilterOptionDisplayText } from 'constants/v2/job-options';
import { useMemo } from 'react';
import { IntlShape } from 'react-intl';

import { FilterConfigs } from 'components/filters/dropdown-section/filter-dropdown/types';

interface CareerSiteFiltersOption extends BaseCareerSiteFiltersOption {
  count?: number;
}

const mapFilterCountsToOptions = (
  counts: Array<{ id: string; count: number }> | undefined,
  options: CareerSiteFiltersOption[] | undefined,
  filterType?: 'jobType' | 'jobHours',
  intl?: IntlShape,
): CareerSiteFiltersOption[] => {
  return (counts ?? []).map(count => {
    const label =
      filterType && intl
        ? getFilterOptionDisplayText(count.id, filterType, intl)
        : (options?.find(opt => opt.key === count.id)?.label ?? count.id);

    return {
      key: count.id,
      label,
      count: count.count,
    };
  });
};

interface UseFilterConfigParams {
  brandSetting: CareerSiteSettings;
  filterOptions?: {
    jobTypes: CareerSiteFiltersOption[];
    jobHours: CareerSiteFiltersOption[];
    countries: CareerSiteFiltersOption[];
    states: CareerSiteFiltersOption[];
    cities: CareerSiteFiltersOption[];
    category: CareerSiteFiltersOption[];
    position: CareerSiteFiltersOption[];
    locationName: CareerSiteFiltersOption[];
  };
  filterCounts?: {
    jobTypes: Array<{ id: string; count: number }>;
    jobHours: Array<{ id: string; count: number }>;
    countries: Array<{ id: string; count: number }>;
    states: Array<{ id: string; count: number }>;
    cities: Array<{ id: string; count: number }>;
    category: Array<{ id: string; count: number }>;
    position: Array<{ id: string; count: number }>;
    locationName: Array<{ id: string; count: number }>;
  };
  intl: IntlShape;
}

export function useFilterConfig({
  brandSetting,
  filterOptions,
  filterCounts,
  intl,
}: UseFilterConfigParams): FilterConfigs {
  return useMemo(
    () => ({
      category: {
        label: brandSetting?.category_filter_label?.length
          ? brandSetting.category_filter_label
          : intl.formatMessage({
              defaultMessage: 'Category',
              description: 'Filter label for job category selection',
            }),
        placeholder: brandSetting?.category_filter_label?.length
          ? intl.formatMessage(
              {
                defaultMessage: 'Select {name}',
                description:
                  'Filter dropdown select placeholder with custom category name',
              },
              { name: brandSetting.category_filter_label.toLowerCase() },
            )
          : intl.formatMessage({
              defaultMessage: 'Select categories',
              description:
                'Filter dropdown select placeholder for category filter',
            }),
        searchPlaceholder: brandSetting?.category_filter_label?.length
          ? intl.formatMessage(
              {
                defaultMessage: 'Search {name}',
                description:
                  'Filter dropdown search placeholder with custom category name',
              },
              { name: brandSetting.category_filter_label.toLowerCase() },
            )
          : intl.formatMessage({
              defaultMessage: 'Search categories',
              description:
                'Filter dropdown search placeholder for category filter',
            }),
        showFilter: brandSetting?.display_category_filter,
        options: mapFilterCountsToOptions(
          filterCounts?.category,
          filterOptions?.category,
        ),
        hasSeparator: false,
      },
      position: {
        label: intl.formatMessage({
          defaultMessage: 'Position',
          description: 'Filter label for position selection',
        }),
        placeholder: intl.formatMessage({
          defaultMessage: 'Select positions',
          description: 'Filter dropdown select placeholder for position filter',
        }),
        searchPlaceholder: intl.formatMessage({
          defaultMessage: 'Search positions',
          description: 'Filter dropdown search placeholder for position filter',
        }),
        showFilter: brandSetting?.display_position_filter,
        options: mapFilterCountsToOptions(
          filterCounts?.position,
          filterOptions?.position,
        ),
        hasSeparator: !brandSetting?.display_location_filter,
      },
      locationName: {
        label: intl.formatMessage({
          defaultMessage: 'Location name',
          description: 'Filter label for location name selection',
        }),
        placeholder: intl.formatMessage({
          defaultMessage: 'Select locations',
          description:
            'Filter dropdown select placeholder for location name filter',
        }),
        searchPlaceholder: intl.formatMessage({
          defaultMessage: 'Search locations',
          description:
            'Filter dropdown search placeholder for location name filter',
        }),
        showFilter: brandSetting?.display_location_filter,
        options: mapFilterCountsToOptions(
          filterCounts?.locationName,
          filterOptions?.locationName,
        ),
        hasSeparator: true,
      },
      jobHours: {
        label: intl.formatMessage({
          defaultMessage: 'Job Hours',
          description: 'Filter label for job hours selection',
        }),
        showFilter: true,
        options: mapFilterCountsToOptions(
          filterCounts?.jobHours,
          filterOptions?.jobHours,
          'jobHours',
          intl,
        ),
        hasSeparator: true,
      },
      jobType: {
        label: intl.formatMessage({
          defaultMessage: 'Job Type',
          description: 'Filter label for job type selection',
        }),
        showFilter: brandSetting?.display_job_type_filter,
        options: mapFilterCountsToOptions(
          filterCounts?.jobTypes,
          filterOptions?.jobTypes,
          'jobType',
          intl,
        ),
        hasSeparator: true,
      },
      country: {
        label: intl.formatMessage({
          defaultMessage: 'Country/Region',
          description: 'Filter label for country/region selection',
        }),
        showFilter: true,
        options: mapFilterCountsToOptions(
          filterCounts?.countries,
          filterOptions?.countries,
        ),
        hasSeparator: true,
      },
      state: {
        label: intl.formatMessage({
          defaultMessage: 'State/Province',
          description: 'Filter label for state/province selection',
        }),
        showFilter: true,
        options: mapFilterCountsToOptions(
          filterCounts?.states,
          filterOptions?.states,
        ),
        hasSeparator: true,
      },
      city: {
        label: intl.formatMessage({
          defaultMessage: 'City',
          description: 'Filter label for city selection',
        }),
        showFilter: true,
        options: mapFilterCountsToOptions(
          filterCounts?.cities,
          filterOptions?.cities,
        ),
        hasSeparator: false,
      },
    }),
    [brandSetting, filterOptions, filterCounts, intl],
  );
}
